import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Form,
  Dropdown,
  Divider,
  Button,
  Grid,
  Input,
  Segment,
  Icon,
  Checkbox,
  Label,
  Header,
  Modal,
  Accordion,
  Menu,
  Popup
} from 'semantic-ui-react';
import PropTypes from 'prop-types';
// Clean architecture: Import from separate filter and data slices
import {
  setBasicFilter,
  setAdvancedFilter,
  toggleGenre,
  clearGenres,
  toggleAuthor,
  clearAuthors,
  clearAllFilters,
  toggleAdvancedFilters,
  setSearchMode,
  setIsSearching,
  saveFavorite,
  loadFavorite,
  deleteFavorite,
  updateFavorite,
  fetchFilterOptions,
  fetchBooksByAuthor,
  fetchCharactersByBook,
  fetchBooksByLitGroup,
  fetchCharactersByLitGroup,
  selectFilters,
  selectFilterOptions,
  selectUI,
  selectFavorites,
  selectActiveFavoriteId,
  selectLoading,
  selectCombinedFiltersMemo as selectCombinedFilters
} from '../../slices/opportunityFiltersSlice';
import { fetchOpportunities, selectOpportunityPaging } from '../../slices/opportunityDataSlice';
import debounce from 'lodash/debounce';

/**
 * Enhanced OpportunityFilter component with genres, advanced filters, and favorites
 */
const OpportunityFilterEnhanced = ({ onSearchInitiated }) => {
  const dispatch = useDispatch();

  // Redux selectors
  const filters = useSelector(selectFilters);
  const filterOptions = useSelector(selectFilterOptions);
  const ui = useSelector(selectUI);
  const favorites = useSelector(selectFavorites);
  const activeFavoriteId = useSelector(selectActiveFavoriteId);
  const loading = useSelector(selectLoading);
  const combinedFilters = useSelector(selectCombinedFilters);

  // Local state
  const [showSaveFavoriteModal, setShowSaveFavoriteModal] = useState(false);
  const [favoriteName, setFavoriteName] = useState('');
  const [showGenreDropdown, setShowGenreDropdown] = useState(false);
  const [genreSearch, setGenreSearch] = useState('');
  const genreDropdownRef = useRef(null);
  const [showAuthorDropdown, setShowAuthorDropdown] = useState(false);
  const [authorSearch, setAuthorSearch] = useState('');
  const authorDropdownRef = useRef(null);

  // Load filter options on mount
  useEffect(() => {
    dispatch(fetchFilterOptions());
  }, [dispatch]);

  // Handle author change - fetch books for all selected authors
  useEffect(() => {
    if (filters.advanced.authorIds && filters.advanced.authorIds.length > 0) {
      // Pass the full array of author IDs - backend now supports multiple authors
      dispatch(fetchBooksByAuthor(filters.advanced.authorIds));
    }
  }, [filters.advanced.authorIds, dispatch]);

  // ⭐ LITERARY GROUP FILLS BOTH DEPENDENT DROPDOWNS.
  //
  // Selecting a group should populate its Books AND its Characters. Before this the only cascade
  // ran author → books → characters, so choosing a Literary Group narrowed the RESULTS while
  // leaving both dropdowns empty — and the character list could not be reached at all, since it
  // was disabled until a Book was picked.
  useEffect(() => {
    if (filters.advanced.litGroupId) {
      dispatch(fetchBooksByLitGroup(filters.advanced.litGroupId));
      dispatch(fetchCharactersByLitGroup(filters.advanced.litGroupId));
    }
  }, [filters.advanced.litGroupId, dispatch]);

  // Handle book change — REFINE the characters to that book.
  //
  // ⚠️ Ordered after the group effect deliberately: when a Book is chosen the book-scoped list
  // replaces the group-scoped one, and when the Book is CLEARED the group's full list is restored
  // rather than left showing one book's cast. Book selection is optional, so clearing it has to be
  // a real state, not a dead end.
  useEffect(() => {
    if (filters.advanced.bookId) {
      dispatch(fetchCharactersByBook(filters.advanced.bookId));
    } else if (filters.advanced.litGroupId) {
      dispatch(fetchCharactersByLitGroup(filters.advanced.litGroupId));
    }
  }, [filters.advanced.bookId, filters.advanced.litGroupId, dispatch]);

  // The debounced search is created once (its useCallback deps are deliberately stable, so the
  // 300ms timer is not torn down on every keystroke). Reading paging through a ref keeps it
  // current inside that long-lived closure without rebuilding the debouncer — a stale captured
  // `limit` would silently send the previous page size.
  const paging = useSelector(selectOpportunityPaging);
  const pagingRef = useRef(paging);
  useEffect(() => { pagingRef.current = paging; }, [paging]);

  // Create debounced search for auto mode
  const debouncedSearch = useCallback(
    debounce((filters) => {
      dispatch(setIsSearching(true));
      // Every search re-enters at offset 0 — a new filter set has a new result set, and holding
      // the old offset would drop the actor onto page 7 of something they have not seen page 1 of.
      // `limit` is also what opts this request into the paginated envelope.
      dispatch(fetchOpportunities({
        ...filters,
        limit: pagingRef.current.limit || 10,
        offset: 0,
        sort: pagingRef.current.sort || 'tier'
      })).finally(() => {
        dispatch(setIsSearching(false));
      });
      if (onSearchInitiated) {
        onSearchInitiated();
      }
    }, 300),
    [dispatch, onSearchInitiated]
  );

  // ---------------------------------------------------------------------------
  // "Search Now" gating
  // ---------------------------------------------------------------------------
  // The Search Now button should only go live when at least one filter has
  // been changed from its default. The defaults below MUST mirror
  // `initialState.filters` in `opportunityFiltersSlice.js` — if you change
  // a default in the slice, change it here too. The mirror is intentional
  // (rather than re-importing `initialState`) because it lets this component
  // remain explicit about *which* fields constitute a "filtered" state.
  //
  // Notes on individual fields:
  //   - status defaults to 'Open' in the slice (not null/'all'), so a status
  //     of 'Open' means "user hasn't touched the filter". Anything else
  //     (Assigned, Completed, Cancelled, or 'all') counts as a deliberate
  //     selection.
  //   - searchTerm is trimmed before comparing — pure whitespace doesn't
  //     count.
  //   - For multi-select arrays (genres, authorIds) we treat empty as the
  //     default; any membership is dirty.
  //   - priority and litGroupId/bookId/characterId default to null; any
  //     truthy value is dirty.
  const filtersAreAtDefault = useMemo(() => {
    const basic = filters.basic || {};
    const advanced = filters.advanced || {};
    const searchTermDirty = (basic.searchTerm || '').trim() !== '';
    const statusDirty = (basic.status || 'Open') !== 'Open';
    const genresDirty = Array.isArray(basic.genres) && basic.genres.length > 0;
    const litGroupDirty = Boolean(advanced.litGroupId);
    const authorsDirty = Array.isArray(advanced.authorIds) && advanced.authorIds.length > 0;
    const bookDirty = Boolean(advanced.bookId);
    const characterDirty = Boolean(advanced.characterId);
    const priorityDirty = Boolean(advanced.priority);
    return !(
      searchTermDirty ||
      statusDirty ||
      genresDirty ||
      litGroupDirty ||
      authorsDirty ||
      bookDirty ||
      characterDirty ||
      priorityDirty
    );
  }, [filters]);

  // Handle search based on mode
  const handleSearch = useCallback(() => {
    if (ui.searchMode === 'manual') {
      // Only search when button is clicked
      console.log('Sending filters to server:', combinedFilters); // Add this

      dispatch(setIsSearching(true));
      dispatch(fetchOpportunities({
        ...combinedFilters,
        limit: paging.limit || 10,
        offset: 0,
        sort: paging.sort || 'tier'
      })).finally(() => {
        dispatch(setIsSearching(false));
      });
      if (onSearchInitiated) {
        onSearchInitiated();
      }
    }
  }, [ui.searchMode, combinedFilters, dispatch, onSearchInitiated, paging.limit, paging.sort]);

  // Handle filter changes with auto-search
  const handleFilterChange = useCallback((field, value, isBasic = true) => {
    if (isBasic) {
      dispatch(setBasicFilter({ field, value }));
    } else {
      dispatch(setAdvancedFilter({ field, value }));
    }

    // Auto search in hybrid/auto mode for dropdowns
    if (ui.searchMode !== 'manual' && field !== 'searchTerm') {
      debouncedSearch(combinedFilters);
    }
  }, [dispatch, ui.searchMode, combinedFilters, debouncedSearch]);

  // Handle search term with Enter key
  const handleSearchTermKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Handle genre toggle
  const handleGenreToggle = (genre) => {
    dispatch(toggleGenre(genre));

    // Auto search in hybrid/auto mode
    if (ui.searchMode !== 'manual') {
      debouncedSearch(combinedFilters);
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (genreDropdownRef.current && !genreDropdownRef.current.contains(event.target)) {
        setShowGenreDropdown(false);
      }
      if (authorDropdownRef.current && !authorDropdownRef.current.contains(event.target)) {
        setShowAuthorDropdown(false);
      }
    };

    if (showGenreDropdown || showAuthorDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showGenreDropdown, showAuthorDropdown]);

  // Filter genres by search
  const filteredGenres = filterOptions.genres.filter(genre =>
    genre.toLowerCase().includes(genreSearch.toLowerCase())
  );

  // Filter authors by search
  const filteredAuthors = filterOptions.authors.filter(author => {
    const fullName = `${author.firstName} ${author.lastName}`.toLowerCase();
    return fullName.includes(authorSearch.toLowerCase());
  });

  // Handle author toggle
  const handleAuthorToggle = (authorId) => {
    dispatch(toggleAuthor(authorId));

    // Auto search in hybrid/auto mode
    if (ui.searchMode !== 'manual') {
      debouncedSearch(combinedFilters);
    }
  };

  // Save current filters as favorite
  const handleSaveFavorite = () => {
    if (favoriteName.trim()) {
      dispatch(saveFavorite({
        name: favoriteName.trim(),
        filters: filters
      }));
      setShowSaveFavoriteModal(false);
      setFavoriteName('');
    }
  };

  // Load a favorite
  const handleLoadFavorite = (favoriteId) => {
    dispatch(loadFavorite(favoriteId));

    // Trigger search after loading favorite
    if (ui.searchMode !== 'manual') {
      debouncedSearch(combinedFilters);
    }
  };

  // Format dropdown options
  const statusOptions = [
    { key: 'all', text: 'All Statuses', value: 'all' },
    ...filterOptions.statuses.map(status => ({
      key: status,
      text: status,
      value: status
    }))
  ];

  const priorityOptions = [
    { key: 'all', text: 'All Priorities', value: 'all' },
    ...filterOptions.priorities.map(priority => ({
      key: priority,
      text: priority,
      value: priority
    }))
  ];


  const bookOptions = [
    { key: 'all-books', text: 'All Books', value: '' },
    ...filterOptions.books
      .filter(book => book.uuid) // Filter out any books with null/undefined uuid
      .map(book => ({
        key: book.uuid || `book-${Math.random()}`, // Fallback key if needed
        text: book.name,
        value: book.uuid,
        description: book.genres?.join(', ')
      }))
  ];

  const characterOptions = [
    { key: 'all-characters', text: 'All Characters', value: '' },
    ...filterOptions.characters
      .filter(character => character.uuid) // Filter out any characters with null/undefined uuid
      .map(character => ({
        key: character.uuid || `char-${Math.random()}`, // Fallback key if needed
        text: character.name,
        value: character.uuid
      }))
  ];

  return (
    <div style={{ position: 'relative', zIndex: 2000 }}>
      {/* Basic Filters */}
      <Segment style={{ position: 'relative', zIndex: 2000 }}>
        <Grid stackable>
          <Grid.Row>
            {/* Search Input */}
            <Grid.Column width={5}>
              <label>Search</label>
              <Input
                fluid
                icon="search"
                placeholder="Search opportunities..."
                value={filters.basic.searchTerm}
                onChange={(e) => handleFilterChange('searchTerm', e.target.value, true)}
                onKeyPress={handleSearchTermKeyPress}
              // 2026-05-14 — Suppressed per operator request. The Search
              // input's `loading` prop swaps its leading icon for a spinner
              // while a search is in flight; combined with the Search Now
              // button's spinner and the full-page marketplace loader
              // (both also suppressed) it produced a visible flash that
              // appeared to push the controls below it down briefly.
              // The button's `disabled` state is enough to prevent
              // double-fires; layout stays still. Re-enable to bring the
              // spinner back.
              // loading={ui.isSearching}
              />
            </Grid.Column>

            {/* Status Filter */}
            <Grid.Column width={3} style={{ position: 'relative', zIndex: 2000 }} >
              <label>Status</label>
              <Dropdown

                fluid
                selection
                options={statusOptions}
                value={filters.basic.status || 'all'}
                onChange={(e, { value }) => handleFilterChange('status', value === 'all' ? null : value, true)}
              />
            </Grid.Column>

            {/* Genre Multi-Select */}
            <Grid.Column width={4}>
              <label>
                Genres
                {filters.basic.genres.length > 0 && (
                  <Label size="tiny" circular color="blue" style={{ marginLeft: '5px' }}>
                    {filters.basic.genres.length}
                  </Label>
                )}
              </label>
              <div ref={genreDropdownRef} style={{ position: 'relative', zIndex: "2000" }}>
                <Button
                  fluid
                  onClick={() => setShowGenreDropdown(!showGenreDropdown)}
                  style={{ textAlign: 'left', display: 'flex', justifyContent: 'space-between' }}
                >
                  {filters.basic.genres.length > 0
                    ? filters.basic.genres.slice(0, 2).join(', ') + (filters.basic.genres.length > 2 ? '...' : '')
                    : 'Select Genres'
                  }
                  <Icon name={showGenreDropdown ? 'chevron up' : 'chevron down'} />
                </Button>

                {showGenreDropdown && (
                  <Segment style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    zIndex: 1000, // Increased z-index to appear above Advanced Filters
                    maxHeight: '300px',
                    overflowY: 'auto',
                    overflowX: 'hidden', // Prevent horizontal scrollbar
                    marginTop: '2px'
                  }}>
                    {/* Clear button - only show if there are selections */}
                    {filters.basic.genres.length > 0 && (
                      <Button
                        size="mini"
                        fluid
                        basic
                        color="red"
                        onClick={(e) => {
                          e.stopPropagation();
                          dispatch(clearGenres());
                          // Auto search after clearing if in auto/hybrid mode
                          if (ui.searchMode !== 'manual') {
                            debouncedSearch(combinedFilters);
                          }
                        }}
                        style={{ marginBottom: '10px' }}
                      >
                        <Icon name="times" /> Clear All Selections
                      </Button>
                    )}

                    <Input
                      fluid
                      size="small"
                      icon="search"
                      placeholder="Search genres..."
                      value={genreSearch}
                      onChange={(e) => setGenreSearch(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      style={{ marginBottom: '10px' }}
                    />
                    {filteredGenres.map(genre => (
                      <div key={genre} style={{ marginBottom: '5px' }}>
                        <Checkbox
                          label={genre}
                          checked={filters.basic.genres.includes(genre)}
                          onChange={() => handleGenreToggle(genre)}
                        />
                      </div>
                    ))}
                    {filteredGenres.length === 0 && (
                      <div style={{ textAlign: 'center', color: '#999' }}>
                        No genres found
                      </div>
                    )}
                  </Segment>
                )}
              </div>
            </Grid.Column>
            {/* Filter select/save controls */}
            <Grid.Column width={4}>
              <span style={{ display: "block" }}>
                Select/Save Filter Favorites
              </span>
              <Button.Group>
                {/* Favorites Dropdown */}
                <Dropdown
                  trigger={
                    <Button icon labelPosition="left" style={{ width: "225px", borderRadius: "4px 4px 4px 4px" }}>
                      <Icon name="star" color={activeFavoriteId ? 'yellow' : null} />
                      Favorites
                    </Button>
                  }
                  pointing="top left"
                  icon={null}
                >
                  <Dropdown.Menu style={{
                    width: "225px" // top-left, top-right, bottom-right, bottom-left
                  }}>
                    <Dropdown.Header>Saved Filters</Dropdown.Header>
                    {favorites.length > 0 ? (
                      favorites.map(favorite => (
                        <Dropdown.Item
                          key={favorite.id}
                          onClick={() => handleLoadFavorite(favorite.id)}
                        >
                          <Icon
                            name="star"
                            color={favorite.id === activeFavoriteId ? 'yellow' : 'grey'}
                          />
                          {favorite.name}
                          {favorite.isDefault && <Label size="tiny">Default</Label>}
                        </Dropdown.Item>
                      ))
                    ) : (
                      <Dropdown.Item disabled>No saved favorites</Dropdown.Item>
                    )}
                    <Dropdown.Divider />
                    <Dropdown.Item onClick={() => setShowSaveFavoriteModal(true)}>
                      <Icon name="plus" />
                      Save Current Filters
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </Button.Group>
            </Grid.Column>
          </Grid.Row>

          {/* Filter Actions Row */}
          <Grid.Row>
            <Grid.Column width={3}>
              <label>Literary Group</label>
              <div style={{ position: 'relative' }}>
                <style>{`.litgroup-filter-dropdown .menu.visible { min-width: calc(100% + 80px) !important; }`}</style>
                <Dropdown
                  className="litgroup-filter-dropdown"
                  fluid
                  search
                  selection
                  clearable
                  options={[
                    { key: 'all-litgroups', text: 'All LitGroups', value: 'all' },
                    ...(filterOptions.litGroups || [])
                      .filter(lg => lg.uuid)
                      .map(lg => ({
                        key: lg.uuid,
                        text: lg.name,
                        value: lg.uuid,
                        description: `${lg.opportunityCount || 0} opps`
                      }))
                  ]}
                  value={filters.advanced.litGroupId || ''}
                  onChange={(e, { value }) => handleFilterChange('litGroupId', value || null, false)}
                  placeholder="Select LitGroup"
                />
              </div>
            </Grid.Column>

            <Grid.Column width={3}>
              <label>
                Authors
                {filters.advanced.authorIds.length > 0 && (
                  <Label size="tiny" circular color="green" style={{ marginLeft: '5px' }}>
                    {filters.advanced.authorIds.length}
                  </Label>
                )}
              </label>
              <div ref={authorDropdownRef} style={{ position: 'relative' }}>
                <Button
                  fluid
                  onClick={() => setShowAuthorDropdown(!showAuthorDropdown)}
                  style={{ textAlign: 'left', display: 'flex', justifyContent: 'space-between' }}
                >
                  {filters.advanced.authorIds.length > 0
                    ? (() => {
                      const selectedAuthors = filterOptions.authors.filter(a =>
                        filters.advanced.authorIds.includes(a.userId)
                      );
                      const names = selectedAuthors.slice(0, 2).map(a =>
                        `${a.firstName} ${a.lastName}`
                      );
                      return names.join(', ') + (selectedAuthors.length > 2 ? '...' : '');
                    })()
                    : 'Select Authors'
                  }
                  <Icon name={showAuthorDropdown ? 'chevron up' : 'chevron down'} />
                </Button>

                {showAuthorDropdown && (
                  <Segment style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    zIndex: 1000,
                    maxHeight: '300px',
                    overflowY: 'auto',
                    overflowX: 'hidden', // Prevent horizontal scrollbar
                    marginTop: '2px'
                  }}>
                    {/* Clear button - only show if there are selections */}
                    {filters.advanced.authorIds.length > 0 && (
                      <Button
                        size="mini"
                        fluid
                        basic
                        color="red"
                        onClick={(e) => {
                          e.stopPropagation();
                          dispatch(clearAuthors());
                          // Auto search after clearing if in auto/hybrid mode
                          if (ui.searchMode !== 'manual') {
                            debouncedSearch(combinedFilters);
                          }
                        }}
                        style={{ marginBottom: '10px' }}
                      >
                        <Icon name="times" /> Clear All Selections
                      </Button>
                    )}

                    <Input
                      fluid
                      size="small"
                      icon="search"
                      placeholder="Search authors..."
                      value={authorSearch}
                      onChange={(e) => setAuthorSearch(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      style={{ marginBottom: '10px' }}
                    />
                    {filteredAuthors.map(author => (
                      <div key={author.userId} style={{ marginBottom: '5px' }}>
                        <Checkbox
                          label={`${author.firstName} ${author.lastName}`}
                          checked={filters.advanced.authorIds.includes(author.userId)}
                          onChange={() => handleAuthorToggle(author.userId)}
                        />
                        {author.opportunityCount > 0 && (
                          <Label size="tiny" basic style={{ marginLeft: '5px' }}>
                            {author.opportunityCount} opportunities
                          </Label>
                        )}
                      </div>
                    ))}
                    {filteredAuthors.length === 0 && (
                      <div style={{ textAlign: 'center', color: '#999' }}>
                        No authors found
                      </div>
                    )}
                  </Segment>
                )}
              </div>
            </Grid.Column>

            <Grid.Column width={3}>
              <label>Book</label>
              <Dropdown
                fluid
                search
                selection
                options={bookOptions}
                value={filters.advanced.bookId}
                onChange={(e, { value }) => handleFilterChange('bookId', value || null, false)}
                placeholder="Select Book"
                // ⚠️ Enabled by a Literary Group OR an author. Gating on authors alone meant a
                // LitGroup could FILL this dropdown and leave it greyed out — the same shape as
                // the Character deadlock one row down, and invisible for the same reason: a
                // disabled control with options in it looks identical to one with none.
                disabled={!filters.advanced.litGroupId && filters.advanced.authorIds.length === 0}
                loading={loading.books}
              />
            </Grid.Column>

            <Grid.Column width={3}>
              <label>Character</label>
              <Dropdown
                fluid
                search
                selection
                options={characterOptions}
                value={filters.advanced.characterId}
                onChange={(e, { value }) => handleFilterChange('characterId', value || null, false)}
                placeholder="Select Character"
                // ⚠️ Enabled by a Literary Group OR a Book. Requiring a Book was a deadlock: the
                // only way to enable this dropdown was also the only way to reach an endpoint
                // that returned nothing, so it was either greyed out or empty and never useful.
                disabled={!filters.advanced.litGroupId && !filters.advanced.bookId}
                loading={loading.characters}
              />
            </Grid.Column>

            <Grid.Column width={3}>
              <label>Priority</label>
              <div style={{ position: 'relative' }}>
                <Dropdown
                  className="priority-filter-dropdown"
                  fluid
                  selection
                  options={priorityOptions}
                  value={filters.advanced.priority || 'all'}
                  onChange={(e, { value }) => handleFilterChange('priority', value === 'all' ? null : value, false)}
                />
              </div>
            </Grid.Column>
          </Grid.Row>
          <Divider style={{ marginTop: "0px", marginBottom: "0px" }} />
          <Grid.Row >
            <Grid.Column width={(9)}>
              <Button onClick={() => dispatch(clearAllFilters())}>
                Clear All
              </Button>
            </Grid.Column>

            <Grid.Column width={(3)} textAlign='right'>

              <Popup
                trigger={
                  <Button

                    icon
                    toggle
                    active={ui.searchMode === 'auto'}
                    onClick={() => dispatch(setSearchMode(ui.searchMode === 'manual' ? 'auto' : 'manual'))}
                  >
                    <span style={{ paddingRight: "6px" }}>{ui.searchMode === 'manual' ? "Manual Search" : "Auto Search"}</span>
                    <Icon name={ui.searchMode === 'auto' ? 'lightning' : 'pointing up'} />
                  </Button>
                }
                content={ui.searchMode === 'auto'
                  ? 'Auto-search mode: Searches automatically as you change filters'
                  : 'Manual mode: Click "Search Now" to search'
                }
                position="bottom center"
              />
            </Grid.Column>

            <Grid.Column width={4} verticalAlign="bottom">
              {/*
                The Search Now button is gated on `filtersAreAtDefault` —
                when nothing has been changed from the slice's defaults the
                button is disabled and the surrounding Popup explains why.
                Wrapping the disabled <Button> in a <span> is necessary
                because Semantic UI's <Popup> can't attach hover handlers
                directly to a disabled native button element (pointer-events
                are blocked).
              */}
              <Popup
                content={
                  filtersAreAtDefault
                    ? 'Adjust at least one filter (search term, status, genre, author, book, character, priority, or literary group) to enable Search Now.'
                    : 'Run the search with the current filter settings.'
                }
                position="top right"
                trigger={
                  <span style={{ display: 'block' }}>
                    <Button
                      primary
                      onClick={handleSearch}
                      // 2026-05-14 — Button spinner suppressed per operator
                      // request. Pair-suppressed with the Search input's
                      // spinner (above) and the full-page loader in
                      // OpportunityMarketplace.jsx. The button continues to
                      // be `disabled` while the fetch is in flight so
                      // double-clicks can't fire a second search; we just
                      // don't render the in-button spinner.
                      // loading={ui.isSearching}
                      disabled={ui.isSearching || filtersAreAtDefault}
                      fluid
                    >
                      <Icon name="search" />
                      Search Now
                    </Button>
                  </span>
                }
              />
            </Grid.Column>
          </Grid.Row>
        </Grid>

      </Segment>

      {/* Save Favorite Modal */}
      <Modal
        open={showSaveFavoriteModal}
        onClose={() => setShowSaveFavoriteModal(false)}
        size="small"
      >
        <Header icon>
          <Icon name="star" />
          Save Filter Configuration
        </Header>
        <Modal.Content>
          <Form>
            <Form.Field>
              <label>Favorite Name</label>
              <Input
                placeholder="e.g., High Priority Horror Projects"
                value={favoriteName}
                onChange={(e) => setFavoriteName(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSaveFavorite()}
              />
            </Form.Field>
            <div style={{ marginTop: '15px', color: '#666' }}>
              <p><strong>Current Filter Settings:</strong></p>
              <ul style={{ fontSize: '0.9em' }}>
                {filters.basic.searchTerm && <li>Search: "{filters.basic.searchTerm}"</li>}
                {filters.basic.status && <li>Status: {filters.basic.status}</li>}
                {filters.basic.genres.length > 0 && <li>Genres: {filters.basic.genres.join(', ')}</li>}
                {filters.advanced.litGroupId && filters.advanced.litGroupId !== 'all' && <li>Literary Group filter applied</li>}
                {filters.advanced.priority && <li>Priority: {filters.advanced.priority}</li>}
                {filters.advanced.authorIds?.length > 0 && <li>{filters.advanced.authorIds.length} Author(s) selected</li>}
                {filters.advanced.bookId && <li>Book filter applied</li>}
                {filters.advanced.characterId && <li>Character filter applied</li>}
              </ul>
            </div>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setShowSaveFavoriteModal(false)}>
            Cancel
          </Button>
          <Button
            color="green"
            onClick={handleSaveFavorite}
            disabled={!favoriteName.trim()}
          >
            <Icon name="checkmark" /> Save
          </Button>
        </Modal.Actions>
      </Modal>
    </div>
  );
};

OpportunityFilterEnhanced.propTypes = {
  onSearchInitiated: PropTypes.func
};

export default OpportunityFilterEnhanced;
